import { ChevronDown } from "lucide-react";

/**
 * MobileTabDropdown
 * -----------------
 * Renders the tab strip as a single native <select>-style dropdown on mobile
 * screens. Replaces the horizontal-scrolling pill row that was hard to reach
 * on phones (users kept missing tabs off-screen).
 *
 * Props:
 *   tabs           : Array<{ key, label, icon?, count?, badge? }>
 *   activeKey      : string
 *   onChange       : (newKey) => void
 *   className?     : optional wrapper class
 *   accentColor?   : Tailwind accent shade (default 'teal')
 */
const MobileTabDropdown = ({ tabs, activeKey, onChange, className = "", accentColor = "teal" }) => {
  const active = tabs.find((t) => t.key === activeKey) || tabs[0];
  const ActiveIcon = active?.icon;

  return (
    <div className={`relative w-full ${className}`} data-testid="mobile-tab-dropdown">
      <select
        value={activeKey}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Select section"
        data-testid="mobile-tab-select"
        className={`w-full appearance-none rounded-xl border border-gray-200 bg-white pl-11 pr-10 py-3 text-sm font-semibold text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-${accentColor}-500 focus:border-${accentColor}-500`}
      >
        {tabs.map((t) => {
          const label = t.count != null ? `${t.label} (${t.count})` : t.label;
          return (
            <option key={t.key} value={t.key}>
              {label}
            </option>
          );
        })}
      </select>
      {/* Leading icon */}
      {ActiveIcon && (
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <ActiveIcon className={`h-4 w-4 text-${accentColor}-600`} />
        </div>
      )}
      {/* Trailing chevron */}
      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </div>
      {/* Optional badge below dropdown (e.g. unit-mismatch alert) */}
      {active?.badge && (
        <div className="mt-2 flex justify-end">{active.badge}</div>
      )}
    </div>
  );
};

export default MobileTabDropdown;
